import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';

import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Dialog, useBreakpointValue } from '@chakra-ui/react';

import { Swiper } from 'swiper';
import { SwiperOptions } from 'swiper/types';
import { v4 as uuid } from 'uuid';

import { ImageRatio, ImageSize } from '@nocowanie/core';
import { Image, Slider } from '@nocowanie/common-ui';

import { GalleryGrid } from '@app/components/organisms/gallery-grid';
import { AppModalsEnum } from '@app/enums';
import { useModalFragmentSync, useWpResizer } from '@app/hooks';
import {
    accommodationStaticSlice,
    commonUiStateSlice,
    useAppDispatch,
    useAppSelector,
} from '@app/state';

import { MainGalleryProps } from './main-gallery.props';

const GalleryModal = dynamic(
    () => import('@app/components/organisms/gallery-modal').then(c => c.GalleryModal),
    { ssr: false },
);

export const MainGallery = ({ ...props }: MainGalleryProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'gallery',
    });
    const dispatch = useAppDispatch();
    const [isSliderReady, setIsSliderReady] = useState(false);
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const isGalleryOpen = useAppSelector(
        commonUiStateSlice.selectors.selectModalData<boolean>(AppModalsEnum.Gallery),
    );
    const { images, name } = useAppSelector(accommodationStaticSlice.selectors.selectGalleryData);
    const modalId = useMemo(() => 'galleryModal-' + uuid(), []);
    const [isPhotoPreviewActive, setIsPhotoPreviewActive] = useState(false);
    const wpresizer = useWpResizer();

    const handleOpenGallery = () => {
        setTimeout(
            () =>
                dispatch(
                    commonUiStateSlice.actions.setModalDataAction({
                        modalName: AppModalsEnum.Gallery,
                        modalData: true,
                    }),
                ),
            0,
        );
    };

    const handleCloseGallery = () => {
        setTimeout(
            () => dispatch(commonUiStateSlice.actions.resetModalDataAction(AppModalsEnum.Gallery)),
            0,
        );
    };

    const imgSize = useBreakpointValue({
        base: ImageSize.Sm,
        sm: ImageSize.Md,
        md: ImageSize.Lg,
    }) as ImageSize;

    const imagesList = useMemo(
        () =>
            images?.map((image, index) => {
                return {
                    id: image.id,
                    body: (
                        <Image
                            src={wpresizer({ ratio: ImageRatio['16/9'], size: imgSize }, image.url)}
                            height="100%"
                            width="100%"
                            objectFit="cover"
                            alt={`${name ? name : t('altTextPlaceholder')} (${index + 1})`}
                            hidden={index > 0 ? !isSliderReady : false}
                            fetchpriority={index > 0 ? undefined : 'high'}
                            loading={index > 0 ? 'lazy' : undefined}
                        />
                    ),
                };
            }),
        [images, imgSize, isSliderReady],
    );

    const sliderParams: SwiperOptions = {
        loop: true,
        pagination: {
            type: 'fraction',
        },
        on: {
            beforeInit(swiper: Swiper) {
                setIsSliderReady(true);
            },
            tap(swiper: Swiper) {
                // prevents the gallery modal from opening if the clicked element is not a slide
                if (!swiper.clickedSlide) {
                    return;
                }

                setTimeout(function () {
                    // prevents occasionally opening photo right after the modal is opened
                    handleOpenGallery();
                }, 100);
            },
        },
        injectStyles: [
            `
            swiper-container {
                height: 100%;
            }
            :host .swiper-pagination {
                text-align: right;
                color: var(--chakra-colors-white);
                width: fit-content;
            }
            :host .swiper-pagination-fraction {
                left: 0;
                right: 0;
                bottom: 1rem;
                margin-left: auto;
                margin-right: auto;
                background-color: rgba(0, 0, 0, 0.3);
                padding: 0.125rem 0.5rem;
                border-radius: 0.5rem;
            }
        `,
        ],
    };

    useEffect(() => {
        if (!isGalleryOpen) {
            return;
        }

        const timeout = setTimeout(function () {
            const el = dialogContentRef.current?.querySelector(
                '.gallery-image-link',
            ) as HTMLAnchorElement;

            if (!el) {
                return;
            }

            // focuses the first photo of the gallery after opening the modal
            el.focus();
        }, 100);

        return () => {
            clearTimeout(timeout);
        };
    }, [isGalleryOpen]);

    useModalFragmentSync(AppModalsEnum.Gallery, isGalleryOpen);
    const dialogContentRef = useRef<HTMLDivElement>(null);

    return (
        <>
            {isMobileBrowser ? (
                <Box aspectRatio={'16 / 9'} {...props}>
                    {imagesList ? (
                        <Slider
                            slides={imagesList}
                            sliderParams={sliderParams}
                            slideMinHeight="100%"
                        />
                    ) : null}
                </Box>
            ) : (
                <GalleryGrid images={images} name={name} onItemClick={handleOpenGallery} />
            )}

            <Dialog.Root
                id={modalId}
                motionPreset="slide-in-bottom"
                open={isGalleryOpen}
                trapFocus={!isPhotoPreviewActive}
                modal={false}
                onEscapeKeyDown={() => (!isPhotoPreviewActive ? handleCloseGallery : undefined)}
                size="full"
                variant="stickyHeader"
                lazyMount={true}
            >
                <GalleryModal
                    images={images}
                    imgSize={imgSize}
                    name={name}
                    onClose={handleCloseGallery}
                    setIsPhotoPreviewActive={setIsPhotoPreviewActive}
                    dialogContentRef={dialogContentRef}
                />
            </Dialog.Root>
        </>
    );
};
